import classNames from 'classnames';
import { useCallback, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { useFormContext } from 'react-hook-form';
import { FiDownload } from 'react-icons/fi';

import Button from '../../../components/Button';
import CheckBoxGroup from '../../../components/form/CheckBoxGroup';
import Input from '../../../components/form/Input';
import RadioGroupButton from '../../../components/form/RadioGroupButton';
import UploadFileBox from '../../../components/UploadFileBox';
import { downloadCSV } from '../../../utils/blob';
import { isSameDate, addDate, startOfDate, endOfDate } from '../../../utils/dayjs';
import { NOTIFICATION_FIELD_KEYS, INotificationFields } from '../validates/notificationSchema';

const CSV_URL_PATH = '../../a/template/notification-user-template.csv';

interface IPropsNotificationPresenter {
  readOnly?: boolean;
  onSelectCSVFile: (file: File | undefined) => void;
  selectedCSVFile: File | undefined;
}

const NotificationDetailPresenter = (props: IPropsNotificationPresenter) => {
  const { selectedCSVFile, onSelectCSVFile, readOnly } = props;

  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext();
  const [sendAt, targetType, segmentType, fileName] = watch([
    NOTIFICATION_FIELD_KEYS.SEND_AT,
    NOTIFICATION_FIELD_KEYS.TARGET_TYPE,
    NOTIFICATION_FIELD_KEYS.TARGET_SEGMENT_TYPE,
    NOTIFICATION_FIELD_KEYS.FILE_NAME,
  ]);

  useEffect(() => {
    if (targetType === 'specific-user') {
      setValue(NOTIFICATION_FIELD_KEYS.PLATFORMS as INotificationFields, ['IOS', 'ANDROID', 'WEB'], {
        shouldValidate: true,
      });
    }
  }, [setValue, targetType]);

  const currentDate = useMemo(() => new Date(), []);

  const onClickDownloadCSVTemplate = useCallback(() => {
    downloadCSV(CSV_URL_PATH, 'notification-user-template.csv');
  }, []);

  // start date
  const getStartDateMinTime = useCallback(() => {
    if (!sendAt || (sendAt && isSameDate(sendAt, currentDate))) {
      return addDate(currentDate, 1, 'minute').toDate();
    }

    return startOfDate(sendAt).toDate();
  }, [currentDate, sendAt]);

  const getStartDateMaxTime = useCallback(() => {
    return endOfDate(sendAt || currentDate).toDate();
  }, [currentDate, sendAt]);

  return (
    <div className="h-full space-y-12">
      <Input
        name={NOTIFICATION_FIELD_KEYS.TITLE}
        register={register}
        label="Title"
        required
        placeholder="title"
        helperText={errors.title?.message}
        isInvalid={errors.title}
        disabled={!!readOnly}
      />
      <Input
        name={NOTIFICATION_FIELD_KEYS.DESCRIPTION}
        register={register}
        label="Description"
        required
        placeholder="description"
        helperText={errors.description?.message}
        isInvalid={errors.description}
        disabled={!!readOnly}
      />
      <Input name={NOTIFICATION_FIELD_KEYS.URL} register={register} label="URL" placeholder="url" helperText={errors.url?.message} isInvalid={errors.url} disabled={!!readOnly} />
      <div className="flex flex-col flex-1">
        <label className="label label-text text-systemGrays02LabelSecondary">
          <span className="label-text text-systemGrays02LabelSecondary">
            Send notification date and time <span className="text-colorsBrandRed01Primary">*</span>
          </span>
        </label>
        <DatePicker
          className={classNames('input input-bordered w-full font-tt text-systemGrays02LabelSecondary font-semibold', {
            '!input-error': errors.sendAt,
          })}
          required
          timeFormat="HH:mm"
          timeCaption="time"
          dateFormat="d MMM yyyy, h:mm aa."
          showTimeSelect
          timeIntervals={1}
          // showYearDropdown
          selectsStart
          selected={sendAt}
          startDate={sendAt}
          minDate={currentDate}
          minTime={getStartDateMinTime()}
          maxTime={getStartDateMaxTime()}
          placeholderText="Send At"
          onChange={(start: Date) => {
            if (start) {
              setValue(NOTIFICATION_FIELD_KEYS.SEND_AT as INotificationFields, start, {
                // shouldValidate: true,
              });
            }
          }}
          disabled={!!readOnly}
        />
      </div>
      {targetType !== 'specific-user' && (
        <CheckBoxGroup
          disabled={targetType === 'specific-user'}
          register={register}
          name={NOTIFICATION_FIELD_KEYS.PLATFORMS}
          required
          label="Platforms"
          options={[
            { label: 'WEB', value: 'WEB' },
            { label: 'IOS', value: 'IOS' },
            { label: 'ANDROID', value: 'ANDROID' },
          ]}
          isInvalid={errors.platforms}
          helperText={errors.platforms?.message}
        />
      )}
      <RadioGroupButton
        required
        options={[
          { value: 'all-users', label: 'All Users' },
          { value: 'specific-user', label: 'Specific User' },
        ]}
        name={NOTIFICATION_FIELD_KEYS.TARGET_TYPE}
        register={register}
        label={'Target Type'}
      />
      {targetType === 'specific-user' && (
        <RadioGroupButton
          required
          options={[
            // { value: 'search', label: 'Search Users' },
            { value: 'upload', label: 'Upload User List' },
          ]}
          name={NOTIFICATION_FIELD_KEYS.TARGET_SEGMENT_TYPE}
          register={register}
          label={'User Segment'}
        />
      )}
      {segmentType === 'upload' && targetType === 'specific-user' && (
        <div className="!mt-0">
          <UploadFileBox
            label="Upload file"
            required
            hasError={!!errors.fileName}
            helperText={errors.fileName?.message || 'Only .csv .xls and .xlsx file type allowed'}
            onSelectFile={onSelectCSVFile}
            acceptFileExtension=".csv, .xls, .xlsx, .CSV, .XLS, .XLSX"
            fileName={fileName ?? selectedCSVFile?.name}
            rightLabelNode={
              <Button color="red" type="ghost" className="text-colorsBrandRed01Primary" onClick={onClickDownloadCSVTemplate}>
                <FiDownload className="text-24" />
                <p className="ml-4">Download CSV Template</p>
              </Button>
            }
            readOnly={!!readOnly}
            fileUrl={undefined}
          />
        </div>
      )}
    </div>
  );
};

export default NotificationDetailPresenter;
